import { useTranslation } from 'react-i18next'
import { ControllerRenderProps, useFormContext } from 'react-hook-form'
import { BetriebsstelleField, Input } from '../../form'
import {
  AuftragOption,
  AuftragOptionen,
  BefehlFormAuftrag,
  BefehlFormField
} from '../Generic'
import { Betriebsstellenart } from '../../../types'
import { useMemo, useContext, useState } from 'react'
import SlideToUnlock from '../../SlideToUnlock'
import MarkAuftraegeAsDoneContext from '../../../contexts/MarkAuftraegeAsDoneContext'
import {
  DEFAULT_AUFTRAG_25_30,
  DEFAULT_AUFTRAG_25_34
} from './schema/befehl25-form.schema'

export interface Auftrag25_30FormProps {
  befehlKennung: string
  fieldPrefix: string
  readOnly: boolean
  displaySlideToUnlock: boolean
  'data-testid': string
  activateEmptyFieldWarnings: boolean
}

export default function Auftrag25_30Form({
  readOnly,
  fieldPrefix,
  displaySlideToUnlock,
  'data-testid': testId,
  activateEmptyFieldWarnings = false
}: Readonly<Auftrag25_30FormProps>) {
  const formContext = useFormContext()
  const { control, formState, watch, resetField } = formContext
  const { errors } = formState
  const fieldErrors = useMemo(() => {
    return errors ?? formState.errors
  }, [errors, formState.errors])

  const { enabled } = watch(`${fieldPrefix}`)
  const auftrag_25_34_enabled = watch('auftrag_25_34.enabled')

  const { t } = useTranslation('ui-befehle')

  const context = useContext(MarkAuftraegeAsDoneContext)
  const befehlFrontendId = context?.befehlFrontendId
  const isQuittiert = context?.isBefehlsnachrichtQuittiert

  const [confirmedEinfahren, setConfirmedEinfahren] = useState<boolean>(
    context?.isDone(befehlFrontendId + '-25_30') || false
  )
  const [confirmedAusfahren, setConfirmedAusfahren] = useState<boolean>(
    context?.isDone(befehlFrontendId + '-25_31') || false
  )

  const updateSlideToUnlockStateEinfahren = (value: boolean) => {
    context?.setMarkedAsDone(befehlFrontendId + '-25_30', value)
    setConfirmedEinfahren(value)
  }
  const updateSlideToUnlockStateAusfahren = (value: boolean) => {
    context?.setMarkedAsDone(befehlFrontendId + '-25_31', value)
    setConfirmedAusfahren(value)
  }

  return (
    <div data-testid={`${testId}-container`} className="flex flex-col gap-1.5">
      <div className="relative">
        {confirmedEinfahren && isQuittiert && (
          <div
            className="absolute z-30 inset-0 bg-white/50 dark:bg-cool-gray-800/65 pointer-events-none"
            data-testid={`${testId}-gray-out-overlay`}
          />
        )}
        <BefehlFormAuftrag
          auftragKennung="25.30"
          fieldName="auftrag_25_30.enabled"
          testId={testId}
          readOnly={readOnly}
          onChange={(checked) => {
            if (!checked) {
              resetField('auftrag_25_34', {
                defaultValue: DEFAULT_AUFTRAG_25_34
              })
              resetField('auftrag_25_30', {
                defaultValue: DEFAULT_AUFTRAG_25_30
              })
            }
          }}
        >
          <div className="flex flex-col flex-wrap items-baseline sm:flex-row w-full gap-0.5">
            <span data-testid={`${testId}-text`}>
              {t('auftraege.25_30.label')}
            </span>

            <BefehlFormField
              name={`${fieldPrefix}.km`}
              label={t('auftraege.25_30.km')}
              control={control}
              readOnly={readOnly}
              activateEmptyWarning={activateEmptyFieldWarnings}
              useTextDisplayInReadOnly={false}
              data-testid={`${testId}-feld-25-30`}
              render={({ field: field_25_31 }) => (
                <div>
                  <Input
                    {...field_25_31}
                    label={t('auftraege.25_30.km')}
                    className="w-full"
                    disabled={!enabled}
                    readOnly={readOnly}
                    activateEmptyWarning={activateEmptyFieldWarnings}
                    data-testid={`${testId}-feld-25-30-inner`}
                    errors={fieldErrors}
                  />
                  <span
                    className={enabled ? '' : 'text-gray-500 opacity-60'}
                    data-testid={`${testId}-feld-25-30-label`}
                  >
                    {t('auftraege.25_30.km_label')}
                  </span>
                </div>
              )}
            />
            <span data-testid={`${testId}-text-in-den`}>
              {t('auftraege.25_30.in_den')}
            </span>

            <BefehlFormField
              name={`${fieldPrefix}.selector`}
              control={control}
              disabled={!enabled}
              label=""
              readOnly={readOnly}
              activateEmptyWarning={activateEmptyFieldWarnings}
              useTextDisplayInReadOnly={false}
              data-testid={`${testId}-feld-25-32-33-selector`}
              render={({ field }: { field: ControllerRenderProps }) => (
                <AuftragOptionen
                  name={`${fieldPrefix}.selector`}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={!enabled}
                  readOnly={readOnly}
                  errors={fieldErrors}
                  data-testid={`${testId}-feld-25-32-33-selector-options`}
                >
                  <AuftragOption
                    value={Betriebsstellenart.Bf}
                    label={t('auftraege.25_30.bf_label')}
                    data-testid={`${testId}-feld-25-32-33-selector-options-bf`}
                    disabled={!enabled}
                  >
                    <BefehlFormField
                      name={`${fieldPrefix}.name`}
                      label={t('auftraege.25_30.bf')}
                      control={control}
                      readOnly={readOnly}
                      activateEmptyWarning={activateEmptyFieldWarnings}
                      data-testid={`${testId}-feld-25-32`}
                      render={({ field }) => (
                        <BetriebsstelleField
                          {...field}
                          label={t('auftraege.25_30.bf')}
                          variant="solid"
                          className="w-full"
                          disabled={!enabled}
                          data-testid={`${testId}-feld-25-32-inner`}
                          errors={fieldErrors}
                          typ={Betriebsstellenart.Bf}
                        />
                      )}
                    />
                  </AuftragOption>
                  <AuftragOption
                    value={Betriebsstellenart.Bft}
                    label={t('auftraege.25_30.bft_label')}
                    data-testid={`${testId}-feld-25-32-33-selector-options-bft`}
                    disabled={!enabled}
                  >
                    <BefehlFormField
                      name={`${fieldPrefix}.name`}
                      control={control}
                      label={t('auftraege.25_30.bft')}
                      readOnly={readOnly}
                      variant="solid"
                      activateEmptyWarning={activateEmptyFieldWarnings}
                      data-testid={`${testId}-feld-25-33`}
                      render={({ field: zmField }) => (
                        <BetriebsstelleField
                          {...zmField}
                          label={t('auftraege.25_30.bft')}
                          className="w-full"
                          data-testid={`${testId}-feld-25-33-inner`}
                          disabled={!enabled}
                          typ={Betriebsstellenart.Bft}
                          errors={errors}
                        />
                      )}
                    />
                  </AuftragOption>
                </AuftragOptionen>
              )}
            />
            <span data-testid={`${testId}-text-einfahren`}>
              {t('auftraege.25_30.einfahren')}
            </span>
          </div>
        </BefehlFormAuftrag>
        {context && displaySlideToUnlock && (
          <SlideToUnlock
            className="pt-0.75 sm:pl-5 sm:pr-5"
            data-testid={`${testId}-abgearbeitet-markieren-slideToUnlock`}
            confirmed={confirmedEinfahren}
            onConfirmedChange={updateSlideToUnlockStateEinfahren}
            variant="secondary-solid"
          />
        )}
      </div>

      {(!readOnly || auftrag_25_34_enabled) && (
        <div className="relative">
          {confirmedAusfahren && isQuittiert && (
            <div
              className="absolute z-30 inset-0 bg-white/50 dark:bg-cool-gray-800/65 pointer-events-none"
              data-testid={`${testId}-gray-out-overlay`}
            />
          )}
          <div className="flex flex-row">
            <div className="w-6 items-baseline h-full pl-2">
              <span className="mt-1" data-testid={`${testId}-text-und`}>
                {t('auftraege.25_30.und')}
              </span>
            </div>

            <div>
              <BefehlFormAuftrag
                auftragKennung="25.34"
                fieldName="auftrag_25_34.enabled"
                testId={`${testId}-auftrag-25-34`}
                readOnly={readOnly}
                disabled={!enabled}
                activateEmptyFieldWarnings={activateEmptyFieldWarnings}
                label=""
              >
                <span data-testid={`${testId}-text-ausfahren`}>
                  {t('auftraege.25_30.ausfahren')}
                </span>
              </BefehlFormAuftrag>
            </div>
          </div>
          {context && displaySlideToUnlock && (
            <SlideToUnlock
              className="pt-0.75 sm:pl-5 sm:pr-5"
              data-testid={`${testId}-abgearbeitet-markieren-slideToUnlock`}
              confirmed={confirmedAusfahren}
              onConfirmedChange={updateSlideToUnlockStateAusfahren}
              variant="secondary-solid"
            />
          )}
        </div>
      )}
    </div>
  )
}
